import React, { useState, useRef, useEffect } from 'react';
import {
  Loader2,
  Plus,
  MessageSquare,
  Trash2,
  Pin,
  PinOff,
  Copy,
} from 'lucide-react';
import type { ThreadListItem } from '../../api/threads';
import { formatDateKo } from '../../utils/date';

export interface PlaygroundThreadListProps {
  sortedThreads: ThreadListItem[];
  threads: ThreadListItem[];
  threadsLoading: boolean;
  selectedThreadId: string | null;
  pinnedThreadIds: Set<string>;
  cloningThreadId: string | null;
  onSelectThread: (threadId: string) => void;
  onCreateThread: () => void;
  onDeleteThread: (threadId: string, e: React.MouseEvent) => void;
  onCloneThread: (threadId: string, e: React.MouseEvent) => void;
  onTogglePin: (threadId: string, e: React.MouseEvent) => void;
  onRename: (threadId: string, title: string) => void;
}

const PlaygroundThreadList: React.FC<PlaygroundThreadListProps> = React.memo(({
  sortedThreads,
  threads,
  threadsLoading,
  selectedThreadId,
  pinnedThreadIds,
  cloningThreadId,
  onSelectThread,
  onCreateThread,
  onDeleteThread,
  onCloneThread,
  onTogglePin,
  onRename,
}) => {
  const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingThreadId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingThreadId]);

  const handleDoubleClick = (thread: ThreadListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingThreadId(thread.thread_id);
    setEditingTitle(thread.title || '');
  };

  const handleEditSave = () => {
    if (editingThreadId && editingTitle.trim()) {
      onRename(editingThreadId, editingTitle.trim());
    }
    setEditingThreadId(null);
  };

  const handleEditCancel = () => {
    setEditingThreadId(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  };
  return (
    <>
      <div className="p-3 border-t border-b border-slate-100 bg-indigo-50 flex items-center justify-between">
        <h4 className="font-semibold text-sm text-indigo-800 flex items-center gap-1">
          <MessageSquare size={14} />
          대화 스레드
        </h4>
        <button
          onClick={onCreateThread}
          data-testid="create-thread"
          className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          title="새 스레드 생성"
        >
          <Plus size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {threadsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-4 text-slate-400">
            <p className="text-xs">스레드가 없습니다</p>
            <button onClick={onCreateThread} className="mt-1 text-indigo-600 hover:underline text-xs">
              새 스레드 만들기
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {sortedThreads.map((thread) => {
              const isPinned = pinnedThreadIds.has(thread.thread_id);
              const isSelected = selectedThreadId === thread.thread_id;

              const activeCount = thread.active_message_count ?? thread.message_count;
              const totalCount = thread.message_count;
              const activeTurns = Math.ceil(activeCount / 2);
              const totalTurns = Math.ceil(totalCount / 2);
              const activeTokens = thread.active_tokens ?? 0;
              const idleMs = thread.last_message_at
                ? Math.max(0, Date.now() - new Date(thread.last_message_at).getTime())
                : 0;
              const idleHours = Math.round((idleMs / (1000 * 60 * 60)) * 100) / 100;

              return (
                <div
                  key={thread.thread_id}
                  data-testid="thread-item"
                  onClick={() => onSelectThread(thread.thread_id)}
                  className={`px-3 py-2 cursor-pointer transition-colors group ${
                    isSelected
                      ? 'bg-indigo-50 border-l-2 border-indigo-500'
                      : isPinned
                        ? 'bg-amber-50/50 hover:bg-amber-50'
                        : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-700 truncate flex items-center gap-1">
                        {isPinned && <Pin size={11} className="text-amber-500 shrink-0" />}
                        {editingThreadId === thread.thread_id ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onKeyDown={handleEditKeyDown}
                            onBlur={handleEditSave}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm text-slate-700 bg-white border border-indigo-300 rounded px-1 py-0 w-full outline-none focus:ring-1 focus:ring-indigo-400"
                          />
                        ) : (
                          <span
                            onDoubleClick={(e) => handleDoubleClick(thread, e)}
                            title="더블 클릭하여 이름 변경"
                          >
                            {thread.title || '제목 없음'}
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[10px] text-slate-400 break-all leading-tight">
                        {thread.thread_id}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5 flex-wrap">
                        <span title={`전체 ${totalTurns} / 활성 ${activeTurns} 턴`}>
                          {activeTurns}/{totalTurns} turns
                        </span>
                        <span title="활성 토큰 (Gemini 실제 사용량)">
                          {activeTokens.toLocaleString()} tok
                        </span>
                        <span title="마지막 메시지 이후 경과 시간">{idleHours}h idle</span>
                        {thread.last_message_at && <span>{formatDateKo(thread.last_message_at)}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={(e) => onTogglePin(thread.thread_id, e)}
                        data-testid="pin-thread"
                        className={`p-1 transition-opacity ${
                          isPinned
                            ? 'text-amber-500 hover:text-amber-600 opacity-100'
                            : 'text-slate-300 hover:text-amber-500 opacity-0 group-hover:opacity-100'
                        }`}
                        title={isPinned ? '고정 해제' : '상단 고정'}
                      >
                        {isPinned ? <PinOff size={14} /> : <Pin size={14} />}
                      </button>
                      <button
                        onClick={(e) => onCloneThread(thread.thread_id, e)}
                        disabled={cloningThreadId !== null}
                        data-testid="clone-thread"
                        className={`p-1 text-slate-300 hover:text-green-600 transition-opacity disabled:cursor-not-allowed ${
                          cloningThreadId === thread.thread_id
                            ? 'opacity-100' // keep the spinner visible even if the mouse leaves the row
                            : 'opacity-0 group-hover:opacity-100 disabled:opacity-30'
                        }`}
                        title="복제 (디버깅용)"
                      >
                        {cloningThreadId === thread.thread_id ? (
                          <Loader2 size={14} className="animate-spin text-green-600" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                      <button
                        onClick={(e) => onDeleteThread(thread.thread_id, e)}
                        className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="삭제"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
});
PlaygroundThreadList.displayName = 'PlaygroundThreadList';

export default PlaygroundThreadList;
